import { Injectable, inject } from '@angular/core';
import { generateClient, type Client } from 'aws-amplify/api';
import { lastValueFrom, Observable } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../environments/environment';

export type GraphqlSubscriptionResult<T> = Observable<GraphqlSubscriptionMessage<T>>;

export interface GraphqlSubscriptionMessage<T> {
    data: T;
}

export interface EscalationsSubscriptionResult {
    subscribe2channel: {
        data: string;
    };
}

@Injectable({
    providedIn: 'root'
})
export class AppsyncService {
    private readonly oidcSecurityService = inject(OidcSecurityService);

    private graphqlClient!: Client;
    private initializingClientPromise?: Promise<Client>;
    private amplifyConfigured = false;

    private async initializeAmplifyAndGenerateClient(): Promise<Client> {
        if (this.graphqlClient) {
            return this.graphqlClient;
        }

        if (this.initializingClientPromise) {
            return this.initializingClientPromise;
        }

        this.initializingClientPromise = import('aws-amplify').then(({ Amplify }) => {
            if (!this.amplifyConfigured) {
                Amplify.configure({
                    API: {
                        GraphQL: {
                            endpoint: environment.awsAppSync.graphqlEndpoint,
                            defaultAuthMode: 'lambda'
                        }
                    }
                });
                this.amplifyConfigured = true;
            }
            const client = generateClient() as unknown as Client;
            this.graphqlClient = client;
            return client;
        });

        try {
            return await this.initializingClientPromise;
        } catch (err) {
            this.initializingClientPromise = undefined;
            throw err;
        }
    }

    public async listenTo<T>(query: string): Promise<GraphqlSubscriptionResult<T>> {
        const [token, client] = await Promise.all([
            lastValueFrom(this.oidcSecurityService.getIdToken()),
            this.initializeAmplifyAndGenerateClient()
        ]);
        return client.graphql({
            query,
            authToken: token
        }) as GraphqlSubscriptionResult<T>;
    }
}
